'use client'

import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Link, useParams } from 'react-router-dom'
import '@/styles/bauhaus.css'
import { useLanguage } from '@/i18n/LanguageProvider'
import { appLanguageToContentLocale } from '@/i18n/contentLocale'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import 'github-markdown-css/github-markdown-dark.css'
import poems from '@/data/poems.json'

export default function PoetPage() {
  const { id = '', poest = '' } = useParams<{ id: string; poest: string }>()
  const [markdownContent, setMarkdownContent] = useState<string>('')
  const { t, language } = useLanguage()
  const lang = appLanguageToContentLocale(language)

  // Decode the poet's name
  const decodedPoest = decodeURIComponent(poest)

  // Get the poet's name based on the language（JSON 仅有中英别名，德语沿用英文展示名）
  const poetName =
    lang === 'zh'
      ? decodedPoest
      : poems[decodedPoest as keyof typeof poems] || decodedPoest

  // Fetch poet's Markdown content from the API route
  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await fetch(`/api/poem/${id}/${encodeURIComponent(decodedPoest)}`)
        if (!response.ok) throw new Error('Failed to fetch document')
        const text = await response.text()
        setMarkdownContent(text)
      } catch (error) {
        setMarkdownContent('Document not found')
      }
    }
    fetchContent()
  }, [id, decodedPoest])

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1C1C1C] to-[#252525]">
      {/* 页面头部 */}
      <div className="pt-20 pb-8 relative bg-gradient-to-b from-[#1C1C1C] to-[#1C1C1C]">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        <div className="absolute w-full h-[200px] bg-[radial-gradient(ellipse_at_center,_rgba(120,120,120,0.1)_0%,rgba(0,0,0,0)_70%)]"></div>
        
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          {/* 返回链接 */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-8"
          >
            <Link 
              to={`/Qiaodong/BPF/china-poetry/${id}`}
              className="inline-flex items-center text-[#B9AE9A]/60 hover:text-[#B9AE9A] transition-colors duration-300 text-sm font-bauhaus"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              {t('bpf.back_to_activities')}
            </Link>
          </motion.div>

          <div className="text-center mb-12">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-5xl font-bauhaus text-white mb-6"
            >
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#B9AE9A] to-[#8C4233]">
                {poetName}
              </span>
            </motion.h1>
          </div>
        </div>
      </div>

      {/* 主要内容区域 */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="mb-16"
        >
          <div className="bg-[#1C1C1C]/30 backdrop-blur-sm border border-[#B9AE9A]/10 rounded-xl overflow-hidden">
            <article className="markdown-body p-8 md:p-12" style={{ background: 'transparent', color: '#FAF9F6' }}>
              <style>{`
                .markdown-body {
                  background-color: transparent !important;
                  color: #FAF9F6 !important;
                }
                .markdown-body h1,
                .markdown-body h2,
                .markdown-body h3,
                .markdown-body h4,
                .markdown-body h5,
                .markdown-body h6 {
                  color: #FAF9F6 !important;
                  border-bottom: none !important;
                  padding-bottom: 0 !important;
                }
                .markdown-body p {
                  color: #FAF9F6 !important;
                }
                .markdown-body ul > li {
                  list-style: none !important;
                  position: relative;
                  padding-left: 1.2em;
                  color: #FAF9F6 !important;
                }
                .markdown-body ul > li::before {
                  content: '-';
                  position: absolute;
                  left: 0;
                  color: inherit;
                }
                .markdown-body a {
                  color: #B9AE9A !important;
                }
                .markdown-body a:hover {
                  color: #FAF9F6 !important;
                }
                .markdown-body code {
                  background-color: rgba(185, 174, 154, 0.1) !important;
                  color: #FAF9F6 !important;
                }
                .markdown-body pre {
                  background-color: rgba(185, 174, 154, 0.1) !important;
                  color: #FAF9F6 !important;
                }
                .markdown-body blockquote {
                  border-left-color: #B9AE9A !important;
                  color: #FAF9F6 !important;
                }
                .markdown-body table {
                  color: #FAF9F6 !important;
                }
                .markdown-body table th,
                .markdown-body table td {
                  border-color: rgba(185, 174, 154, 0.2) !important;
                }
                .markdown-body hr {
                  border-color: rgba(185, 174, 154, 0.2) !important;
                }
              `}</style>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{markdownContent}</ReactMarkdown>
            </article>
          </div>
        </motion.div>
      </div>
    </div>
  )
} 